import React, { useEffect, useMemo, useState, lazy, Component } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Search, MapPin, Plus, Minus, ShoppingBag, LayoutGrid, List, UtensilsCrossed, ClipboardList, ChevronRight, ChevronDown, X, LogIn, Star, Navigation } from 'lucide-react';
import { useCategories } from './CategoriesContext';
import { useStock } from './StockContext';
import { useBranches } from './BranchesContext';
import { useOutlets } from './OutletsContext';
import { useCustomerAuth } from './CustomerAuthContext';
import { useCart } from './CartContext';
import { useQrSession, QrContextType } from './QrSessionContext';
import { AuthSheet } from './AuthSheet';
import { ItemDetailSheet } from './ItemDetailSheet';
import { BranchStatusBadge } from './BranchStatus';
type ViewMode = 'list' | 'grid';
function formatETB(n: number) {
  return `ETB ${Math.round(n).toLocaleString()}`;
}
export function CustomerMenu() {
  const {
    branchId,
    branches,
    setBranchId
  } = useBranches();
  const {
    isOutOfStock
  } = useStock();
  const {
    outlets,
    getOutletMenu,
    setOutletId
  } = useOutlets();
  const {
    isAuthenticated,
    customer
  } = useCustomerAuth();
  const {
    lines,
    count: cartCount,
    total: cartTotal,
    addItem,
    decrement
  } = useCart();
  const {
    setQrSession
  } = useQrSession();
  const searchParams = new URLSearchParams(window.location.search);
  const urlOutletId = searchParams.get('outlet');
  // Allow previewing a specific branch via ?branch=<id> (e.g. a dine-in-only
  // branch to test the online-ordering-not-available capture state). Switches
  // the shared active branch, which the checkout flow reads too.
  const urlBranchId = searchParams.get('branch');
  useEffect(() => {
    if (urlBranchId && branches.some(b => b.id === urlBranchId)) {
      setBranchId(urlBranchId);
    }
  }, [urlBranchId, branches, setBranchId]);
  // Parse QR context
  const urlCtx = searchParams.get('ctx') as QrContextType;
  const urlTable = searchParams.get('table');
  useEffect(() => {
    if (urlCtx === 'table' || urlCtx === 'takeaway') {
      setQrSession(urlCtx, urlTable ? parseInt(urlTable, 10) : null);
    }
  }, [urlCtx, urlTable, setQrSession]);
  const branchOutlets = outlets.filter(o => o.branchId === branchId);
  const activeOutlet = urlOutletId ? branchOutlets.find(o => o.id === urlOutletId) || branchOutlets[0] : branchOutlets[0];
  useEffect(() => {
    if (activeOutlet) setOutletId(activeOutlet.id);
  }, [activeOutlet, setOutletId]);
  const branch = branches.find(b => b.id === branchId) || branches[0];
  const accent = activeOutlet?.color || 'orange';
  const outletMenu = activeOutlet ? getOutletMenu(activeOutlet.id) : [];
  const navigate = useNavigate();
  const switchableBranches = branches.filter(b => b.active);
  const canSwitchLocation = switchableBranches.length > 1;
  const [locationSheetOpen, setLocationSheetOpen] = useState(false);
  const handleSwitchLocation = (id: string) => {
    if (id === branchId) {
      setLocationSheetOpen(false);
      return;
    }
    if (cartCount > 0 && !window.confirm('Switching location will start a new order for that branch. Your current cart may not be available there. Continue?')) {
      return;
    }
    setBranchId(id);
    setLocationSheetOpen(false);
    // Reset to the new branch (drop any pinned outlet) so its default menu loads.
    navigate(`/menu?branch=${id}`);
  };
  // UI state
  const [view, setView] = useState<ViewMode>('list');
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  // Guest add-to-cart triggers an inline sign-in sheet; the tapped item is held
  // here and added once authentication completes.
  const [authSheetOpen, setAuthSheetOpen] = useState(false);
  const [pendingItem, setPendingItem] = useState<{
    itemId: string;
    name: string;
    basePrice: number;
    modifiers: {
      id: string;
      name: string;
      price: number;
    }[];
    notes?: string;
    qty: number;
  } | null>(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<(typeof outletMenu)[0] | null>(null);
  const filteredMenu = useMemo(() => {
    const q = query.trim().toLowerCase();
    return outletMenu.filter(i => q ? i.name.toLowerCase().includes(q) : true);
  }, [outletMenu, query]);
  const menuData = useMemo(() => {
    const grouped: Record<string, typeof filteredMenu> = {};
    filteredMenu.forEach(item => {
      if (!grouped[item.category]) grouped[item.category] = [];
      grouped[item.category].push(item);
    });
    return Object.entries(grouped).map(([category, items]) => ({
      category,
      items
    }));
  }, [filteredMenu]);
  const visibleSections = activeCategory === 'all' ? menuData : menuData.filter(s => s.category === activeCategory);
  // Guests get the inline sign-in sheet; authenticated users add directly.
  const handleAddItem = (item: {
    itemId: string;
    name: string;
    basePrice: number;
    modifiers: {
      id: string;
      name: string;
      price: number;
    }[];
    notes?: string;
  }, qty: number = 1) => {
    if (isAuthenticated) {
      for (let i = 0; i < qty; i++) {
        addItem(item);
      }
    } else {
      setPendingItem({
        ...item,
        qty
      });
      setAuthSheetOpen(true);
    }
  };
  const handleSheetAuthenticated = () => {
    if (pendingItem) {
      for (let i = 0; i < pendingItem.qty; i++) {
        addItem({
          itemId: pendingItem.itemId,
          name: pendingItem.name,
          basePrice: pendingItem.basePrice,
          modifiers: pendingItem.modifiers,
          notes: pendingItem.notes
        });
      }
    }
    setPendingItem(null);
    setAuthSheetOpen(false);
  };
  return <div className="min-h-screen w-full bg-[#f7f7f5] font-sans pb-28" data-id="element-1587">
      {/* Utility bar */}
      <div className="bg-[#0f172a] text-white" data-id="element-1588">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between text-xs" data-id="element-1589">
          <Link to="/" className="flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors" data-id="element-1590">
            <ArrowLeft size={13} data-id="element-1591" />
            Back
          </Link>
          <div className="flex items-center gap-4 text-gray-400" data-id="element-1592">
            <span className="hidden xs:flex items-center gap-1.5" data-id="element-1593">
              <MapPin size={12} data-id="element-1594" /> {branch?.name}
            </span>
            <span className="flex items-center gap-1.5 text-emerald-400" data-id="element-1595">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" data-id="element-1596" />
              Open now
            </span>
          </div>
        </div>
      </div>

      {/* Brand header */}
      <header className="bg-white border-b border-gray-100" data-id="element-1597">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 flex items-center gap-4" data-id="element-1598">
          {activeOutlet?.branding?.logoUrl ? <img src={activeOutlet.branding.logoUrl} alt="" className="w-14 h-14 rounded-2xl object-cover shadow-sm flex-shrink-0" data-id="element-1599" /> : <div className={`w-14 h-14 rounded-2xl flex items-center justify-center bg-${accent}-100 text-${accent}-600 flex-shrink-0`} data-id="element-1600">
              <UtensilsCrossed size={26} data-id="element-1601" />
            </div>}
          <div className="min-w-0" data-id="element-1602">
            <h1 className="text-xl sm:text-2xl font-extrabold text-gray-900 tracking-tight truncate" data-id="element-1603">
              {activeOutlet?.name || branch?.name || 'Our Menu'}
            </h1>
            {canSwitchLocation ? <button onClick={() => setLocationSheetOpen(true)} className="mt-0.5 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors max-w-full" data-id="element-1604">
                <MapPin size={14} className="flex-shrink-0" data-id="element-1605" />
                <span className="truncate font-medium" data-id="element-1606">{branch?.name}</span>
                <ChevronDown size={14} className="flex-shrink-0" data-id="element-1607" />
              </button> : <p className="text-sm text-gray-500 truncate" data-id="element-1608">
                {activeOutlet?.description || 'Browse the menu and order'}
              </p>}
          </div>
          {isAuthenticated ? <div className="ml-auto flex items-center gap-2 flex-shrink-0" data-id="element-1609">
              <Link to="/orders" className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors" data-id="element-1610">
                <ClipboardList size={16} data-id="element-1611" />
                <span className="hidden sm:inline" data-id="element-1612">Orders</span>
              </Link>
              <span className={`w-9 h-9 rounded-full flex items-center justify-center bg-${accent}-100 text-${accent}-700 text-sm font-bold`} title={customer?.name} data-id="element-1613">
                {customer?.name?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div> : <button onClick={() => {
          setPendingItem(null);
          setAuthSheetOpen(true);
        }} className={`ml-auto flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white bg-${accent}-500 hover:bg-${accent}-600 transition-colors flex-shrink-0`} data-id="element-1614">
              <LogIn size={16} data-id="element-1615" />
              Login
            </button>}
        </div>
      </header>

      {/* Loyalty Banner */}
      {isAuthenticated && customer && <div className="bg-white border-b border-gray-100" data-id="element-1616">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4" data-id="element-1617">
            <LoyaltyCard customer={customer} accent={accent} data-id="element-1618" />
          </div>
        </div>}

      {/* Sticky search + category nav */}
      <div className="sticky top-0 z-20 bg-[#f7f7f5]/95 backdrop-blur border-b border-gray-200" data-id="element-1619">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 space-y-3" data-id="element-1620">
          <div className="flex items-center gap-2" data-id="element-1621">
            <div className="relative flex-1" data-id="element-1622">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1623" />
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search the menu…" className={`w-full pl-10 pr-4 py-2.5 rounded-xl bg-white border border-gray-200 text-sm focus:outline-none focus:border-${accent}-400 focus:ring-2 focus:ring-${accent}-100 transition`} data-id="element-1624" />
            </div>
            <div className="flex items-center bg-white border border-gray-200 rounded-xl p-0.5 flex-shrink-0" data-id="element-1625">
              <button onClick={() => setView('list')} aria-label="List view" aria-pressed={view === 'list'} className={`p-2 rounded-lg transition-colors ${view === 'list' ? `bg-${accent}-500 text-white` : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1626">
                <List size={16} data-id="element-1627" />
              </button>
              <button onClick={() => setView('grid')} aria-label="Grid view" aria-pressed={view === 'grid'} className={`p-2 rounded-lg transition-colors ${view === 'grid' ? `bg-${accent}-500 text-white` : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1628">
                <LayoutGrid size={16} data-id="element-1629" />
              </button>
            </div>
          </div>

          <div className="relative" data-id="element-1630">
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide" data-id="element-1631">
              <CategoryPill label="All items" active={activeCategory === 'all'} accent={accent} onClick={() => setActiveCategory('all')} data-id="element-1632" />
              {menuData.map(section => <CategoryPill key={section.category} label={section.category} active={activeCategory === section.category} accent={accent} onClick={() => setActiveCategory(section.category)} data-id="element-1633" />)}
            </div>
            {/* fade hint on the right edge */}
            <div className="pointer-events-none absolute right-0 top-0 bottom-1 w-8 bg-gradient-to-l from-[#f7f7f5] to-transparent" data-id="element-1634" />
          </div>
        </div>
      </div>

      {/* Menu */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-10" data-id="element-1635">
        {visibleSections.length === 0 && <div className="text-center py-24" data-id="element-1636">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 text-gray-400 flex items-center justify-center mx-auto mb-3" data-id="element-1637">
              <Search size={24} data-id="element-1638" />
            </div>
            <p className="text-sm font-semibold text-gray-900" data-id="element-1639">
              No dishes found
            </p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-1640">
              Try a different search or category.
            </p>
          </div>}

        {visibleSections.map(section => <section key={section.category} className="scroll-mt-32" data-id="element-1641">
            <div className="flex items-baseline justify-between mb-4" data-id="element-1642">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-1643">
                {section.category}
              </h2>
              <span className="text-xs font-medium text-gray-400" data-id="element-1644">
                {section.items.length}{' '}
                {section.items.length === 1 ? 'item' : 'items'}
              </span>
            </div>

            <div className={view === 'grid' ? 'grid grid-cols-2 gap-3 sm:gap-4' : 'space-y-3'} data-id="element-1645">
              {section.items.map(item => {
            const itemLines = lines.filter(l => l.itemId === item.id);
            const totalQty = itemLines.reduce((sum, l) => sum + l.qty, 0);
            return <MenuItemCard key={item.id} item={item} view={view} accent={accent} outOfStock={isOutOfStock(item.name)} qty={totalQty} onAdd={() => {
              if (item.modifierIds && item.modifierIds.length > 0) {
                setSelectedItem(item);
                setDetailSheetOpen(true);
              } else {
                handleAddItem({
                  itemId: item.id,
                  name: item.name,
                  basePrice: item.price,
                  modifiers: []
                });
              }
            }} onRemove={() => {
              // If there are multiple lines for this item (different modifiers),
              // we just decrement the first one we find.
              if (itemLines.length > 0) {
                decrement(itemLines[0].lineId);
              }
            }} onCardClick={() => {
              if (item.modifierIds && item.modifierIds.length > 0) {
                setSelectedItem(item);
                setDetailSheetOpen(true);
              }
            }} data-id="element-1646" />;
          })}
            </div>
          </section>)}
      </main>

      {/* Sticky cart bar */}
      <AnimatePresence data-id="element-1647">
        {isAuthenticated && cartCount > 0 && <motion.div initial={{
        y: 80,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 80,
        opacity: 0
      }} transition={{
        type: 'spring',
        stiffness: 400,
        damping: 32
      }} className="fixed bottom-0 inset-x-0 z-30 p-3 sm:p-4" data-id="element-1648">
            <Link to="/checkout" className={`max-w-md mx-auto flex items-center gap-3 pl-4 pr-3 py-3 rounded-2xl bg-${accent}-500 text-white shadow-xl shadow-${accent}-500/20 hover:bg-${accent}-600 transition-colors`} data-id="element-1649">
              <div className="relative" data-id="element-1650">
                <ShoppingBag size={22} data-id="element-1651" />
                <span className={`absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-white text-${accent}-600 text-[11px] font-bold flex items-center justify-center`} data-id="element-1652">
                  {cartCount}
                </span>
              </div>
              <span className="font-semibold text-sm" data-id="element-1653">View cart</span>
              <span className="ml-auto font-bold tabular-nums" data-id="element-1654">
                {formatETB(cartTotal)}
              </span>
              <ChevronRight size={18} className="opacity-80" data-id="element-1655" />
            </Link>
          </motion.div>}
      </AnimatePresence>

      <AuthSheet open={authSheetOpen} accent={accent} itemName={pendingItem?.name} onClose={() => setAuthSheetOpen(false)} onAuthenticated={handleSheetAuthenticated} data-id="element-1656" />

      <ItemDetailSheet open={detailSheetOpen} item={selectedItem} accent={accent} onClose={() => setDetailSheetOpen(false)} onAdd={handleAddItem} data-id="element-1657" />

      <AnimatePresence data-id="element-1658">
        {locationSheetOpen && <div className="fixed inset-0 z-50 flex items-end justify-center" data-id="element-1659">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setLocationSheetOpen(false)} className="absolute inset-0 bg-black/40" data-id="element-1660" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 320
        }} className="relative w-full max-w-md bg-white rounded-t-3xl shadow-2xl max-h-[85vh] flex flex-col" data-id="element-1661">
              <div className="pt-3 pb-1 flex justify-center" data-id="element-1662">
                <span className="w-10 h-1.5 rounded-full bg-gray-300" data-id="element-1663" />
              </div>
              <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100" data-id="element-1664">
                <h2 className="text-xl font-bold text-gray-900" data-id="element-1665">
                  Switch Location
                </h2>
                <button onClick={() => setLocationSheetOpen(false)} className="p-1 text-gray-400 hover:text-gray-700 transition-colors" aria-label="Close" data-id="element-1666">
                  <X size={24} data-id="element-1667" />
                </button>
              </div>
              <ul className="overflow-y-auto" data-id="element-1668">
                {switchableBranches.map(b => {
              const isCurrent = b.id === branchId;
              const chips = [b.methods?.dineIn && 'Dine-in', b.methods?.takeout && 'Takeout', b.methods?.delivery && 'Delivery', b.methods?.orderAhead && 'Order ahead'].filter(Boolean) as string[];
              return <li key={b.id} className={`border-b border-gray-100 ${isCurrent ? `bg-${accent}-50` : ''}`} data-id="element-1669">
                      <button onClick={() => handleSwitchLocation(b.id)} className={`w-full text-left px-6 pt-4 pb-2 transition-colors ${isCurrent ? '' : 'hover:bg-gray-50'}`} data-id="element-1670">
                        <div className="flex items-center gap-2" data-id="element-1671">
                          <span className="font-bold text-gray-900 text-base" data-id="element-1672">
                            {b.name}
                          </span>
                          {isCurrent && <span className={`text-[10px] font-bold uppercase tracking-wide text-${accent}-700 bg-${accent}-100 rounded-full px-2 py-0.5`} data-id="element-1673">
                              Current
                            </span>}
                        </div>
                        <div className="text-sm text-gray-400 mt-0.5" data-id="element-1674">
                          {b.address || '—'}
                        </div>
                        <div className="mt-1.5" data-id="element-1675">
                          <BranchStatusBadge branch={b} data-id="element-1676" />
                        </div>
                        {chips.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-1677">
                            {chips.map(c => <span key={c} className="text-[11px] font-semibold text-gray-600 bg-gray-100 rounded-full px-2 py-0.5" data-id="element-1678">
                                {c}
                              </span>)}
                          </div>}
                      </button>
                      {b.address && <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${b.name} ${b.address}`)}`} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 px-6 pb-3 text-sm font-semibold text-${accent}-700 hover:text-${accent}-800 transition-colors`} data-id="element-1679">
                          <Navigation size={14} data-id="element-1680" />
                          Get directions
                        </a>}
                    </li>;
            })}
              </ul>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}
function LoyaltyCard({
  customer,
  accent
}: {
  customer: {
    points?: number;
    visits?: number;
  };
  accent: string;
}) {
  const points = customer.points || 0;
  let tier = 'Bronze';
  let nextTier = 'Silver';
  let nextTierPoints = 200;
  let progress = points / 200 * 100;
  let tierColor = 'text-amber-700';
  let tierBg = 'bg-amber-100';
  if (points >= 500) {
    tier = 'Gold';
    nextTier = 'Max Tier';
    nextTierPoints = points;
    progress = 100;
    tierColor = 'text-yellow-700';
    tierBg = 'bg-yellow-100';
  } else if (points >= 200) {
    tier = 'Silver';
    nextTier = 'Gold';
    nextTierPoints = 500;
    progress = (points - 200) / 300 * 100;
    tierColor = 'text-slate-700';
    tierBg = 'bg-slate-200';
  }
  return <div className={`bg-${accent}-50 rounded-2xl p-4 border border-${accent}-100 flex flex-col sm:flex-row sm:items-center gap-4`} data-id="element-1681">
      <div className="flex items-center gap-3 flex-1" data-id="element-1682">
        <div className={`w-12 h-12 rounded-full ${tierBg} ${tierColor} flex items-center justify-center flex-shrink-0 shadow-sm`} data-id="element-1683">
          <Star size={24} className={points >= 500 ? 'fill-current' : ''} data-id="element-1684" />
        </div>
        <div data-id="element-1685">
          <div className="flex items-center gap-2" data-id="element-1686">
            <h3 className="font-bold text-gray-900" data-id="element-1687">{points} Points</h3>
            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${tierBg} ${tierColor}`} data-id="element-1688">
              {tier}
            </span>
          </div>
          <p className="text-xs text-gray-600 mt-0.5" data-id="element-1689">
            {tier === 'Gold' ? "You've reached the highest tier!" : `${nextTierPoints - points} points to ${nextTier}`}
          </p>
        </div>
      </div>

      {tier !== 'Gold' && <div className="flex-1 max-w-xs w-full" data-id="element-1690">
          <div className="flex justify-between text-xs font-semibold text-gray-500 mb-1.5" data-id="element-1691">
            <span data-id="element-1692">{tier}</span>
            <span data-id="element-1693">{nextTier}</span>
          </div>
          <div className="h-2 w-full bg-white rounded-full overflow-hidden border border-gray-200" data-id="element-1694">
            <div className={`h-full bg-${accent}-500 rounded-full transition-all duration-1000 ease-out`} style={{
          width: `${Math.min(100, Math.max(0, progress))}%`
        }} data-id="element-1695" />
          </div>
        </div>}
    </div>;
}
function CategoryPill({
  label,
  active,
  accent,
  onClick
}: {
  label: string;
  active: boolean;
  accent: string;
  onClick: () => void;
}) {
  return <button onClick={onClick} className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-semibold transition-colors flex-shrink-0 ${active ? `bg-${accent}-500 text-white` : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-1696">
      {label}
    </button>;
}
function MenuItemCard({
  item,
  view,
  accent,
  outOfStock,
  qty,
  onAdd,
  onRemove,
  onCardClick
}: {
  item: {
    name: string;
    price: number;
    description?: string;
    image?: string;
  };
  view: ViewMode;
  accent: string;
  outOfStock: boolean;
  qty: number;
  onAdd: () => void;
  onRemove: () => void;
  onCardClick?: () => void;
}) {
  const stepper = <div className={`flex items-center gap-2 rounded-full bg-${accent}-500 text-white pl-1 pr-1 py-1`} onClick={e => e.stopPropagation()} data-id="element-1697">
      <button onClick={onRemove} aria-label="Remove one" className="w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors" data-id="element-1698">
        <Minus size={15} data-id="element-1699" />
      </button>
      <span className="text-sm font-bold tabular-nums w-4 text-center" data-id="element-1700">
        {qty}
      </span>
      <button onClick={onAdd} aria-label="Add one" className="w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors" data-id="element-1701">
        <Plus size={15} data-id="element-1702" />
      </button>
    </div>;
  const addBtn = <button onClick={e => {
    e.stopPropagation();
    onAdd();
  }} disabled={outOfStock} className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors ${outOfStock ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : `bg-${accent}-50 text-${accent}-600 hover:bg-${accent}-100`}`} data-id="element-1703">
      <Plus size={15} data-id="element-1704" />
      Add
    </button>;
  if (view === 'grid') {
    return <div onClick={onCardClick} className={`bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col ${outOfStock ? 'opacity-60' : ''} ${onCardClick ? 'cursor-pointer hover:border-gray-200 transition-colors' : ''}`} data-id="element-1705">
        <div className={`aspect-[4/3] bg-${accent}-50 flex items-center justify-center text-${accent}-200 overflow-hidden`} data-id="element-1706">
          {item.image ? <img src={item.image} alt={item.name} loading="lazy" className="w-full h-full object-cover" data-id="element-1707" /> : <UtensilsCrossed size={30} data-id="element-1708" />}
        </div>
        <div className="p-3 flex flex-col flex-1" data-id="element-1709">
          <h3 className="font-bold text-gray-900 text-sm leading-snug line-clamp-2" data-id="element-1710">
            {item.name}
          </h3>
          {item.description && <p className="text-xs text-gray-500 line-clamp-2 mt-1" data-id="element-1711">
              {item.description}
            </p>}
          <div className="mt-3 flex items-center justify-between gap-2" data-id="element-1712">
            <span className="font-bold text-gray-900 text-sm tabular-nums" data-id="element-1713">
              {formatETB(item.price)}
            </span>
            {outOfStock ? <span className="text-[10px] uppercase tracking-wide font-bold text-rose-600" data-id="element-1714">
                Sold out
              </span> : qty > 0 ? stepper : addBtn}
          </div>
        </div>
      </div>;
  }
  return <div onClick={onCardClick} className={`bg-white p-3 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-3.5 ${outOfStock ? 'opacity-60' : ''} ${onCardClick ? 'cursor-pointer hover:border-gray-200 transition-colors' : ''}`} data-id="element-1715">
      {item.image ? <img src={item.image} alt={item.name} loading="lazy" className="w-20 h-20 rounded-xl object-cover flex-shrink-0" data-id="element-1716" /> : <div className={`w-20 h-20 rounded-xl flex items-center justify-center bg-${accent}-50 text-${accent}-200 flex-shrink-0`} data-id="element-1717">
          <UtensilsCrossed size={24} data-id="element-1718" />
        </div>}
      <div className="flex-1 min-w-0" data-id="element-1719">
        <div className="flex items-center gap-2" data-id="element-1720">
          <h3 className="font-bold text-gray-900 leading-snug" data-id="element-1721">{item.name}</h3>
          {outOfStock && <span className="text-[10px] uppercase tracking-wide text-rose-600 bg-rose-100 px-1.5 py-0.5 rounded font-bold flex-shrink-0" data-id="element-1722">
              Sold out
            </span>}
        </div>
        {item.description && <p className="text-sm text-gray-500 line-clamp-2 mt-0.5" data-id="element-1723">
            {item.description}
          </p>}
        <div className="mt-2 font-bold text-gray-900 tabular-nums" data-id="element-1724">
          {formatETB(item.price)}
        </div>
      </div>
      <div className="flex-shrink-0" data-id="element-1725">
        {outOfStock ? null : qty > 0 ? stepper : addBtn}
      </div>
    </div>;
}